import { queryOptions, useQuery } from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { dsBrandDocumentKeys, type DsBrandDocumentGenerateStatusResponse } from './entity';

/**
 * Design Studio brand document read queries.
 *
 * The brand DOCUMENT read now flows through the shared composite hook
 * `useDsGlobalStyles().data.brand` (ds-global-styles/queries) — one query the
 * Brand and Global Styles pages share. What remains here is the authoring-run
 * status poll: a non-suspense `useQuery` whose options factory the caller
 * composes with its own `refetchInterval` / `enabled` (polling is intentionally
 * NOT baked in, mirroring `ds-global-styles`' extract/process status reads).
 */

/* ------------------------------------------------------------------------- */
/* Authoring-run status poll                                                  */
/* ------------------------------------------------------------------------- */

export interface DsBrandDocumentGenerateStatusParams {
  workflowId: string;
  runId: string;
}

/** @public */
export async function getDsBrandDocumentGenerateStatus(
  workspaceId: number,
  params: DsBrandDocumentGenerateStatusParams,
): Promise<DsBrandDocumentGenerateStatusResponse> {
  return hydraUiApi.dsBrandDocument.getGenerateStatus({
    params: {
      path: { environment_id: String(workspaceId) },
      query: { workflow_id: params.workflowId, run_id: params.runId },
    },
  });
}

export const getDsBrandDocumentGenerateStatusQueryOptions = (
  workspaceId: number,
  params: DsBrandDocumentGenerateStatusParams,
) =>
  queryOptions({
    queryKey: dsBrandDocumentKeys.generateStatus(workspaceId, params.workflowId, params.runId),
    queryFn: () => getDsBrandDocumentGenerateStatus(workspaceId, params),
    // Always refetch — the consumer drives cadence via `refetchInterval`.
    staleTime: 0,
  });

interface UseDsBrandDocumentGenerateStatusOptions {
  params: DsBrandDocumentGenerateStatusParams;
  queryConfig?: QueryConfig<typeof getDsBrandDocumentGenerateStatusQueryOptions>;
}

/**
 * Non-suspense poll (unlike the GET above): the consumer gates the fetch with
 * `queryConfig: { enabled }` and sets `queryConfig: { refetchInterval }` to
 * poll. Polling is not baked in so the caller owns start/stop — same contract
 * as `useDsGlobalStylesProcessStatus`.
 *
 * @public
 */
export const useDsBrandDocumentGenerateStatus = ({ params, queryConfig }: UseDsBrandDocumentGenerateStatusOptions) => {
  const workspaceId = requireWorkspaceId();
  return useQuery({
    ...getDsBrandDocumentGenerateStatusQueryOptions(workspaceId, params),
    ...queryConfig,
  });
};
